import type { Command } from '../types/command';

/**
 * Compute the label to display for a command in the listbox, plus an
 * optional parent breadcrumb prefix.
 *
 * Breadcrumb shows when the command has a `parent` AND we are not
 * currently inside that parent's scope. Examples:
 *
 *   - Deep search hit from main mode: child of "Shortcuts" matches
 *     the query; we render "Shortcuts > <name>".
 *   - Deep search hit from a sibling scope: same shape.
 *
 * No breadcrumb when:
 *
 *   - The command has no parent (top-level).
 *   - The user has already entered the parent's scope; rendering its
 *     name on every row would be redundant.
 *   - The parent isn't in the registry (defensive; shouldn't happen
 *     given the orphan-parent invariant, but the helper stays pure).
 */
export function getDisplayLabel(
  command: Command,
  registry: Record<string, Command>,
  rootCommandId: string | null,
): { label: string; parentLabel?: string } {
  if (command.parent === undefined) {
    return { label: command.label };
  }
  if (command.parent === rootCommandId) {
    return { label: command.label };
  }
  const parent = registry[command.parent];
  if (parent === undefined) {
    return { label: command.label };
  }
  // Prefer parent.breadcrumbLabel when set; falls back to parent.label
  // so existing call sites need no changes.
  return { label: command.label, parentLabel: parent.breadcrumbLabel ?? parent.label };
}
